import React, { useState } from 'react'
import Seo from '../components/Seo'
import { ArrowRight, Shield, Calendar, CheckCircle, Menu, X, ChevronDown, ChevronUp } from "lucide-react"
import logoImage from '../assets/logo/logo.png'
import { track } from '../utils/analytics'
import { showConsentSettings } from '../utils/cookieConsentConfig'

const Home: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)
  const [isDemoOpen, setIsDemoOpen] = useState(false)

  const toggleFaq = (index: number, question: string) => {
    const willOpen = openFaqIndex !== index
    setOpenFaqIndex(willOpen ? index : null)
    if (willOpen) {
      track('faq_expand', { q: question })
    }
  }

  return (
    <div id="top" className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <Seo
        title="2homes.app — co-parenting calendar for parents and caregivers"
        description="Co-parenting calendar for parents and caregivers: shared schedule and secure communication. 2homes.app works on every device."
        canonical="https://2homes.app/"
        openGraph={{ type: 'website' }}
      />
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center">
              <img src={logoImage} alt="2homes logo" className="h-8 w-auto mr-3" />
              <span className="font-bold text-xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">2homes</span>
            </a>

            {/* Desktop Menu */}
            <div className="hidden sm:flex items-center space-x-2">
              <a href="/blog" className="px-2 text-sm text-slate-700 hover:text-slate-900 hover:underline" onClick={() => track('cta_click', { label: 'blog_nav' })}>Blog</a>
              <a href="/pricing" className="px-2 text-sm text-slate-700 hover:text-slate-900 hover:underline" onClick={() => track('cta_click', { label: 'pricing_nav' })}>Pricing</a>
              <a href="/auth/login" className="glass-button px-4 py-2 text-sm rounded-md" onClick={() => track('cta_click', { label: 'login_nav' })}>Log in</a>
            </div>

            {/* Mobile menu button */}
            <div className="sm:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-600 hover:text-slate-800"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu: overlay + animated panel (fixed, over content) */}
          <div
            className={`sm:hidden fixed inset-x-0 top-16 bottom-0 z-[55] bg-black/30 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden={!mobileMenuOpen}
          />
          <div
            className={`sm:hidden fixed inset-x-0 top-16 z-[60] bg-white/95 backdrop-blur-sm border-t border-white/20 transform transition-all duration-300 ease-out ${mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}
            role="menu"
            aria-label="Mobile navigation"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a href="/blog" className="w-full justify-center mb-2 inline-flex items-center text-center rounded-md text-slate-700 hover:text-slate-900" onClick={() => { track('cta_click', { label: 'blog_nav_mobile' }); setMobileMenuOpen(false) }}>Blog</a>
              <a href="/pricing" className="w-full justify-center mb-2 inline-flex items-center text-center rounded-md text-slate-700 hover:text-slate-900" onClick={() => { track('cta_click', { label: 'pricing_nav_mobile' }); setMobileMenuOpen(false) }}>Pricing</a>
              <a href="/auth/login" className="glass-button w-full justify-center mb-2 inline-flex items-center text-center rounded-md" onClick={() => { track('cta_click', { label: 'login_nav_mobile' }); setMobileMenuOpen(false) }}>Log in</a>
            </div>
          </div>
        </div>
      </nav>



      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 items-center">
            {/* Left Column - Content */}
            <div className="space-y-6 lg:space-y-8 order-1 lg:order-1 lg:col-span-2">
              <div className="space-y-4 lg:space-y-6">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight lg:leading-[1.2] flex flex-wrap items-center gap-3 justify-center text-center lg:justify-start lg:text-left">
                  <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
                    Online Co-parenting Calendar
                  </span>
                </h1>

                <p className="hidden lg:block text-lg sm:text-xl text-slate-600 leading-relaxed max-w-lg">
                  2homes.app — fast, clear co-parenting schedule. Two homes, one shared plan.
                </p>
              </div>

              {/* Mobile Hero Image */}
              <div className="block lg:hidden">
                <div className="relative mx-auto w-full max-w-[156px] sm:max-w-[220px]">
                  <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-tr from-blue-400/30 via-purple-400/20 to-pink-400/20 blur-2xl"></div>
                  <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                    <img
                      src="/screen.jpg"
                      alt="2homes screenshot"
                      width="640" height="1280"
                      className="block w-full h-auto select-none"
                      loading="eager"
                    />
                  </div>
                </div>
              </div>

              {/* Features List */}
              <div id="features" className="hidden lg:block space-y-3 lg:space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Co-parenting calendar</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Intuitive interface for parents and caregivers</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">✓ Free 14-day Pro trial (no card required)</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Neutral space for communication.</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Convenient mobile app or PWA — install without Google Play or App Store</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col gap-3 pt-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="/auth/register" className="neon-button group px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg w-full sm:w-auto rounded-xl inline-flex items-center justify-center" onClick={() => track('cta_click', { label: 'register_hero' })}>
                    Create free account
                    <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                  <a href="#faq" className="glass-button px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg backdrop-blur-sm bg-white/20 border-white/30 w-full sm:w-auto rounded-xl inline-flex items-center justify-center" onClick={() => track('cta_click', { label: 'faq_hero' })}>
                    FAQ
                  </a>
                </div>
                <p className="text-sm font-bold text-slate-700 ml-1">Includes 14-day Pro trial • No credit card required</p>
              </div>
            </div>

            {/* Right Column - App Screenshot */}
            <div className="relative order-2 lg:order-2 lg:col-span-1 hidden lg:block">
              <div className="relative mx-auto w-full max-w-[240px] sm:max-w-sm lg:max-w-[16rem]">
                {/* Glow background */}
                <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-tr from-blue-400/30 via-purple-400/20 to-pink-400/20 blur-2xl"></div>
                <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 transition-transform duration-300 hover:-rotate-1 hover:scale-[1.01]">
                  <img
                    src="/screen.jpg"
                    alt="2homes screenshot"
                    width="640" height="1280"
                    className="block w-full h-auto select-none"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating shapes */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-r from-pink-400/20 to-red-400/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      {/* Video Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-10">
        <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white/70 backdrop-blur">
          <div className="aspect-video w-full">
            <iframe
              src="https://www.youtube.com/embed/BRr6g93ZjPg?rel=0&modestbranding=1&playsinline=1"
              title="2homes.app – demo"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </div>
      </div>

      {/* Mobile Intro Text and Features (below video) */}
      <div className="lg:hidden max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <p className="text-lg text-slate-600 leading-relaxed mb-4">
          2homes.app — fast, clear co-parenting schedule. Two homes, one shared plan.
        </p>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-slate-700 text-sm sm:text-base">Co-parenting calendar</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-slate-700 text-sm sm:text-base">Intuitive interface for parents and caregivers</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-slate-700 text-sm sm:text-base">Secure data encryption</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-slate-700 text-sm sm:text-base">Neutral space for communication.</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-slate-700 text-sm sm:text-base">Convenient mobile app or PWA — install without Google Play or App Store</span>
          </div>
        </div>
      </div>

      {/* Blog Teasers Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">From the blog</h2>
          <a href="/blog" className="text-blue-600 hover:underline text-sm" onClick={() => track('cta_click', { label: 'blog_home_more' })}>View all</a>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { slug: 'shared-parenting-practice', title: 'Co-parenting — practice', desc: 'Basics, models, and daily life.', img: '/blog/opieka-naprzemienna-praktyka/hero.webp' },
            { slug: 'parenting-plan-shared-care', title: 'Parenting plan — how to prepare', desc: 'Plan elements, examples, tools.', img: '/blog/plan-wychowawczy-opieka-naprzemienna/hero.webp' },
            { slug: '10-shared-parenting-challenges', title: '10 problems and how to solve them', desc: 'Conflicts, schedules, costs — practical tips.', img: '/blog/10-problemow-opieka-naprzemienna/hero.webp' },
          ].map(card => (
            <a key={card.slug} href={`/blog/${card.slug}`} className="group rounded-xl overflow-hidden border bg-white/70 backdrop-blur hover:shadow-lg transition">
              <div className="relative aspect-[16/9] bg-slate-100">
                <img src={card.img} alt={card.title} width="1280" height="720" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-slate-900 group-hover:underline">{card.title}</h3>
                <p className="text-slate-600 text-sm mt-1">{card.desc}</p>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent mb-4 mx-auto">
            Key Features
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Simple co-parenting calendar for parents and caregivers: shared schedule, parenting plan, and secure communication. 2homes.app works on every device. <span className="font-bold text-slate-900">Experience full power with a 14-day free trial.</span>
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* 1. Shared Calendar */}
          <div className="card-glass p-8 text-center group hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-slate-800">Shared Calendar</h3>
            <p className="text-slate-600 leading-relaxed">
              Invite the other parent and viewers. Clear schedule with caregiver markers.
            </p>
          </div>

          {/* 2. Notes & Expenses */}
          <div className="card-glass p-8 text-center group hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-slate-800">Notes & Expenses</h3>
            <p className="text-slate-600 leading-relaxed">
              Add events, save notes, and track expenses in one place. Color coding and clear daily list help you manage the plan quickly.
            </p>
          </div>

          {/* 3. Reports */}
          <div className="card-glass p-8 text-center group hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-slate-800">Reports</h3>
            <p className="text-slate-600 leading-relaxed">
              Expense Report and Care Report (days per child, percentages). Document the month and settlements in a few clicks. Data export to JSON available in every plan.
            </p>
          </div>

          {/* 4. Security */}
          <div className="card-glass p-8 text-center group hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-slate-800">Security</h3>
            <p className="text-slate-600 leading-relaxed">
              End‑to‑end encryption protects your family's privacy. Your data remains yours alone.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="card-glass p-12 text-center">
          <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
            Create a free account or watch demo
          </h2>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Join families who trust 2homes.app. <span className="font-bold text-slate-900">Start with a free 14-day trial of Pro features — no credit card needed.</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/auth/register" className="neon-button px-8 py-4 text-lg w-full sm:w-auto rounded-xl inline-flex items-center justify-center" onClick={() => track('cta_click', { label: 'register_cta' })}>Create free account</a>
            <a
              href="#"
              className="glass-button px-8 py-4 text-lg w-full sm:w-auto rounded-xl inline-flex items-center justify-center"
              onClick={(e) => {
                e.preventDefault()
                track('view_demo', { label: 'demo_cta' })
                setIsDemoOpen(true)
              }}
            >
              Watch demo
            </a>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">FAQ</h2>
        <div className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white/60 backdrop-blur-sm">
          {/* Item 1 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 0}
              onClick={() => toggleFaq(0, 'Is 2homes.app free?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Is 2homes.app free?</span>
              {openFaqIndex === 0 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 0 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Yes — the Basic (Solo) plan is available for every registered user and includes: access for 1 parent, calendar for unlimited children, and mini history (last 5 entries). Paid plans (Pro and Founder) offer more: calendars for unlimited children, access for 2 parents and up to 3 viewers, events, notes and expenses, 180-day history, unlimited calendar horizon, and ready reports (expenses and care). Founder plan is a lifetime deal ($99 one-time) with all Pro features.
              </div>
            )}
          </div>
          {/* Item 2 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 1}
              onClick={() => toggleFaq(1, 'Is my data secure?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Is my data secure?</span>
              {openFaqIndex === 1 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 1 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Yes. We encrypt data at rest and in transit. You have full export of your data on request.
              </div>
            )}
          </div>
          {/* Item 3 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 2}
              onClick={() => toggleFaq(2, 'Does 2homes.app replace a parenting plan?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Does 2homes.app replace a parenting plan?</span>
              {openFaqIndex === 2 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 2 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                No, but it helps stick to arrangements and document changes.
              </div>
            )}
          </div>
          {/* Item 4 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 3}
              onClick={() => toggleFaq(3, 'How to install the app on phone?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">How to install the app on phone?</span>
              {openFaqIndex === 3 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 3 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                In Settings → Installation you will find a dedicated button to install the app. On Android in Chrome choose "Add to Home screen". On iOS in Safari tap "Share" → "Add to Home Screen". 2homes.app is a PWA and works like an app on most newer Android and iOS systems, but we don't guarantee PWA mode works on every device. You can always use the calendar as a website.
              </div>
            )}
          </div>
          {/* Item 5 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 4}
              onClick={() => toggleFaq(4, 'Can I invite the other parent and viewers?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Can I invite the other parent and viewers?</span>
              {openFaqIndex === 4 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 4 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Yes. In Family Settings use Invitations and assign the appropriate role (parent, viewer).
              </div>
            )}
          </div>
          {/* Item 6 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 5}
              onClick={() => toggleFaq(5, 'When do I get access to services?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">When exactly do I get access to services?</span>
              {openFaqIndex === 5 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 5 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                The Basic plan is available immediately after registration. Access to paid plan functions (Pro and Founder) is activated immediately after payment is confirmed by the payment operator.
              </div>
            )}
          </div>
          {/* Item 7 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 6}
              onClick={() => toggleFaq(6, 'Can I export my data?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Can I export my data?</span>
              {openFaqIndex === 6 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 6 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Yes. You have full export of your data on request.
              </div>
            )}
          </div>
          {/* Item 8 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 7}
              onClick={() => toggleFaq(7, 'How much does it cost?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">How much does it cost?</span>
              {openFaqIndex === 7 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 7 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                The Basic (Solo) plan is free and available for every registered user. Details can be found on the <a href="/pricing" className="text-blue-600 hover:underline">Pricing</a> page.
              </div>
            )}
          </div>
          {/* Item 9 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 8}
              onClick={() => toggleFaq(8, 'How to contact support?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">How to contact support?</span>
              {openFaqIndex === 8 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 8 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Write to us at: <a href="mailto:contact@2homes.app" className="text-blue-600 hover:underline">contact@2homes.app</a> or use the <a href="/contact" className="text-blue-600 hover:underline">contact form</a>.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-300">
            &copy; {new Date().getFullYear()} 2homes.app - Co-parenting calendar for parents and caregivers
          </p>
          <div className="mt-3 flex items-center justify-center gap-2 text-slate-400 text-xs flex-wrap">
            <a href="/terms" className="hover:underline">Terms</a>
            <span>•</span>
            <a href="/privacy-policy" className="hover:underline">Privacy Policy</a>
            <span>•</span>
            <button
              type="button"
              className="hover:underline bg-transparent border-0 text-xs text-slate-400 cursor-pointer p-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                showConsentSettings();
              }}
            >
              Manage consent
            </button>
            <span>•</span>
            <a href="/contact" className="hover:underline">Contact</a>
          </div>
        </div>
      </footer>
      {isDemoOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsDemoOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] max-w-3xl aspect-video">
            <button
              aria-label="Close"
              className="absolute -top-3 -right-3 bg-white rounded-full shadow p-2 text-slate-700 hover:text-slate-900"
              onClick={() => setIsDemoOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
            <iframe
              src="https://www.youtube.com/embed/BRr6g93ZjPg?autoplay=1&rel=0"
              title="2homes.app – demo"
              className="w-full h-full rounded-2xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default Home 
